import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IResponse, ITestDriveVehicleInventory, ISyncStatus, SourceSystem } from '@signal/asp-data-commons';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { DealerState } from '../../store/dealer/dealer.reducer';
import { selectDealerCode } from '../../store/dealer/dealer.selectors';
import { environment } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class InventoryVehiclesService {
  private apiRoot: string;

  constructor(private readonly http: HttpClient,
              private readonly dealerState: Store<DealerState>) {
    this.apiRoot = environment.aspCatalogApi;
    }

    AddNewVehicle(vehicle: ITestDriveVehicleInventory): Observable<IResponse<ITestDriveVehicleInventory>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.post(`${this.apiRoot}vehicle-inventory/${dealerCode}/newVehicle`, vehicle).pipe(
            map((data: IResponse<ITestDriveVehicleInventory>) => {
                return data;
            })
        )
      }));
    }

    getAllVehicles(offset: number, limit: number, isSearch: boolean, searchConfig?: any ): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        let searchQuery = '';
        if(isSearch) {
        searchQuery += searchConfig?.modelFilter ?  `&model=${searchConfig?.modelFilter}` : '';
        searchQuery += searchConfig?.statusFilter ?  `&status=${searchConfig?.statusFilter}` : '';
        searchQuery += searchConfig?.nextTestDriveDateFilter ?  `&nextTestDriveDate=${searchConfig?.nextTestDriveDateFilter}` : '';
        searchQuery += searchConfig?.yearFilter ?  `&year=${searchConfig?.yearFilter}` : '';
        searchQuery += searchConfig?.makeFilter ?  `&make=${searchConfig?.makeFilter}` : '';
        searchQuery += searchConfig?.vinFilter ?  `&vin=${searchConfig?.vinFilter}` : '';
        searchQuery += searchConfig?.licensePlateFilter ?  `&licensePlate=${searchConfig?.licensePlateFilter}` : '';
        searchQuery += searchConfig?.healthStatusFilter ?  `&health=${searchConfig?.healthStatusFilter}` : '';
        searchQuery += searchConfig?.odometerFilter ?  `&odometer=${searchConfig?.odometerFilter}` : '';
        searchQuery += searchConfig?.sourceSystemFilter ? `&source=${searchConfig?.sourceSystemFilter}` : '';
        }

        return this.http.get(`${this.apiRoot}vehicle-inventory/${dealerCode}/allVehicles?offset=${offset}&limit=${limit}${searchQuery}`).pipe(
            map((data: IResponse<any>) => {
              return data;
            })
        )
      }));
    }

    getAllVehiclesFromFleetIventory(view: string): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        const url = view === SourceSystem.NVI ? `${this.apiRoot}vehicle-inventory/${dealerCode}/all-nvi-eligible-vehilces?pageSize=200&pageNo=1` 
        : (view === SourceSystem.UVI ? `${this.apiRoot}vehicle-inventory/${dealerCode}/all-uvi-eligible-vehilces?pageSize=200&pageNo=1`: `${this.apiRoot}vehicle-inventory/${dealerCode}/allVehicles`);
        return this.http.get(url).pipe(
            map((data: IResponse<any>) => {
              return data;
            })
          )
        }));
    }

    UpdateVehicle(vehicle: ITestDriveVehicleInventory): Observable<IResponse<ITestDriveVehicleInventory>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.put(`${this.apiRoot}vehicle-inventory/${dealerCode}/${vehicle.vin}`, vehicle).pipe(
            map((data: IResponse<ITestDriveVehicleInventory>) => {
                return data;
            })
        )
      }));
    }

    updateNviStatus(unitId,status){
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.put(`${this.apiRoot}vehicle-inventory/${dealerCode}/appointment-availability/update`, {unitId,status}).pipe(
            map((data: any) => {
                return data;
            })
        )
      }));
    }

    updateUVIStatus(vin,status){
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.put(`${this.apiRoot}vehicle-inventory/${dealerCode}/update-availability/${vin}`, {status}).pipe(
            map((data: any) => {
                return data;
            })
        )
      }));
    }

    updateBulkVehicleStatus(payload: any[], status: boolean, source: string): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      return this.http.post(`${this.apiRoot}vehicle-inventory/${dealerCode}/bulk-update-status`, { status, source, payload }).pipe(
            map((data: IResponse<any>) => {
                return data;
            })
        )
      }));
    }

    deleteVehicle(vehicles: any[]): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        const vins = vehicles.map(x=> x.vin);
        return this.http.put(`${this.apiRoot}vehicle-inventory/${dealerCode}/deleteFleetVehicles/update`, {vins}).pipe(
            map((data: IResponse<any>) => {
                return data;
            })
        )
      }));
    }

    getVehicleInvnetorySyncStatus(): Observable<IResponse<ISyncStatus>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.get(`${this.apiRoot}vehicle-inventory/${dealerCode}/vehicle-inventory-sync-status`).pipe(
        map((data: IResponse<ISyncStatus>) => {
          return data;
        })
      )
      }));
    }
  
    updateVehicleInventoryFromCrm(): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        return this.http.get(`${this.apiRoot}vehicle-inventory/${dealerCode}/initiate-sync`).pipe(
        map((data: IResponse<any>) => {
          return data;
        })
      )
      }));
    }

    restoreVehicle(vehicles: any[]): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        const vins = vehicles.map(x=> x.vin);
        return this.http.patch(`${this.apiRoot}vehicle-inventory/${dealerCode}/restoreVehicles`, {vins}).pipe(
            map((data: IResponse<any>) => {
                return data;
            })
        )
      }));
    }

    getVehiclesFromSource(source:string): Observable<IResponse<any>> {
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
        const url =`${this.apiRoot}vehicle-inventory/${dealerCode}/all-testdrive-availalble-vehilces/${source}` ;
        return this.http.get(url).pipe(
            map((data: IResponse<any>) => {
              return data;
            })
          )
        }));
      }

}
